import React, { useState, useEffect } from 'react';
import { Save, Trash2, Plus, Edit2, Check, X, Layers, Copy, CheckSquare, Square } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { GhostButton } from '../ui/GhostButton';
import { PrimaryButton } from '../ui/PrimaryButton';
import { ConfirmationModal } from '../ui/ConfirmationModal';

// --- DATA DEFINITIONS ---

const SECTIONS = [
    {
        id: 'general',
        label: 'General Info',
        fields: [
            { id: 'sku', label: 'SKU' },
            { id: 'model_name', label: 'Model Name' },
            { id: 'ean', label: 'EAN / Barcode' },
            { id: 'description', label: 'Description' },
            { id: 'color', label: 'Color' },
            { id: 'category', label: 'Category' },
            { id: 'status', label: 'Status' },
            { id: 'image_url', label: 'Main Image URL' },
            { id: 'is_accessory', label: 'Is Accessory' }
        ]
    },
    {
        id: 'charges',
        label: 'Charges',
        fields: [
            { id: 'batch_number', label: 'Charge ID / Batch' },
            { id: 'status', label: 'Status' },
            { id: 'comments', label: 'Comments' },
            { id: 'created_at', label: 'Creation Date' }
        ]
    },
    {
        id: 'properties',
        label: 'Properties',
        fields: [
            { id: 'group_name', label: 'Group Name' },
            { id: 'name', label: 'Property Name' },
            { id: 'value', label: 'Value' }
        ]
    },
    {
        id: 'languages',
        label: 'Languages',
        fields: [
            { id: 'language', label: 'Language Name' },
            { id: 'sw_status', label: 'SW Status' },
            { id: 'um_status', label: 'UM Status' }
        ]
    },
    {
        id: 'packaging',
        label: 'Packaging',
        fields: [
            { id: 'group_name', label: 'Packaging Group' },
            { id: 'property', label: 'Dimension/Weight Type' },
            { id: 'value', label: 'Value' }
        ]
    },
    {
        id: 'pictures',
        label: 'Pictures',
        fields: [
            { id: 'file_name', label: 'File Name' },
            { id: 'file_size', label: 'File Size' },
            { id: 'is_main', label: 'Is Main Image' },
            { id: 'preview', label: 'Image Preview (Embed)' }
        ]
    },
    {
        id: 'accessories',
        label: 'Accessories',
        fields: [
            { id: 'accessory_sku', label: 'Accessory SKU' },
            { id: 'accessory_name', label: 'Accessory Name' },
            { id: 'quantity', label: 'Quantity' }
        ]
    },
    {
        id: 'software',
        label: 'Software & Res.',
        fields: [
            { id: 'name', label: 'File Name' },
            { id: 'type', label: 'Type' },
            { id: 'version', label: 'Version' },
            { id: 'file_path', label: 'Download Link' }
        ]
    }
];

export const ExportProfilesTab = ({ onSelectProfile }) => {
    const { toast } = useToast();
    const [view, setView] = useState('list'); // 'list' | 'edit'
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentProfile, setCurrentProfile] = useState(null);
    const [deleteModalState, setDeleteModalState] = useState({ isOpen: false, profileId: null });
    // Fetch Profiles
    const fetchProfiles = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/export-profiles', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProfiles(data);
            }
        } catch (err) {
            console.error("Failed to fetch profiles", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfiles();
    }, []);

    // Actions
    const handleCreate = () => {
        setCurrentProfile({
            name: '',
            description: '',
            output_style: 'html_table',
            template: 'datasheet_html.hbs',
            config: {
                sections: [] // Will now be array of objects: { id: 'general', fields: ['sku', 'model'] }
            }
        });
        setView('edit');
    };

    const handleEdit = (profile) => {
        // Ensure config exists and structure is valid
        const safeProfile = {
            ...profile,
            config: profile.config || { sections: [] }
        };
        setCurrentProfile(safeProfile);
        setView('edit');
    };

    const handleDeleteClick = (e, id) => {
        e.stopPropagation();
        console.log("Delete clicked for", id);
        setDeleteModalState({ isOpen: true, profileId: id });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModalState.profileId) return;

        try {
            const res = await fetch(`/api/export-profiles/${deleteModalState.profileId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (res.ok) {
                toast.success("Profile deleted successfully");
                fetchProfiles();
                if (currentProfile && currentProfile.id === deleteModalState.profileId) {
                    setCurrentProfile(null);
                    setView('list');
                }
            } else {
                toast.error("Failed to delete profile");
            }
        } catch (err) {
            console.error("Delete failed", err);
            toast.error("Error deleting profile");
        }
        setDeleteModalState({ isOpen: false, profileId: null });
    };

    const handleDuplicate = async (profile) => {
        const newProfile = {
            ...profile,
            name: `${profile.name} (Copy)`,
            id: undefined
        };
        setCurrentProfile(newProfile);
        setView('edit');
    };



    const handleSave = async (profileData) => {
        if (!profileData.name.trim()) {
            toast.error("Profile Name is required");
            return;
        }

        try {
            const url = profileData.id ? `/api/export-profiles/${profileData.id}` : '/api/export-profiles';
            const method = profileData.id ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(profileData)
            });

            if (res.ok) {
                fetchProfiles();
                setView('list');
                setCurrentProfile(null);
                toast.success(profileData.id ? "Profile updated" : "Profile created");
            } else {
                toast.error("Failed to save profile");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error saving profile");
        }
    };

    if (view === 'edit') {
        return (
            <ProfileEditor
                initialProfile={currentProfile}
                onSave={handleSave}
                onCancel={() => { setView('list'); setCurrentProfile(null); }}
            />
        );
    }

    return (
        <div className="flex flex-col h-full bg-stone-900 border border-stone-800 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-stone-950 border-b border-stone-800 flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">My Export Profiles</h3>
                <PrimaryButton onClick={handleCreate} className="gap-2">
                    <Plus size={16} /> Create Profile
                </PrimaryButton>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {loading ? (
                    <div className="text-center text-stone-500 py-10">Loading profiles...</div>
                ) : profiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-stone-500">
                        <Layers size={48} className="mb-4 opacity-20" />
                        <p className="text-lg font-medium text-stone-400">No export profiles created yet.</p>
                        <p className="text-sm mb-6">Create a profile to save and reuse custom export configurations.</p>
                        <PrimaryButton onClick={handleCreate}>Create Profile</PrimaryButton>
                    </div>
                ) : (
                    profiles.map(profile => (
                        <div key={profile.id} className="group bg-stone-950 border border-stone-800 rounded-xl p-4 hover:border-stone-700 transition-colors flex items-center justify-between">
                            <div>
                                <h4 className="font-bold text-white mb-1 flex items-center gap-2">
                                    {profile.name}
                                    <span className="text-[10px] bg-stone-800 text-stone-400 px-1.5 py-0.5 rounded border border-stone-700 uppercase tracking-wider">Custom Profile</span>
                                </h4>
                                <p className="text-sm text-stone-500 line-clamp-1">{profile.description || "No description"}</p>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="flex gap-1 mr-4">
                                    <button onClick={() => handleEdit(profile)} className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded" title="Edit">
                                        <Edit2 size={16} />
                                    </button>
                                    <button onClick={() => handleDuplicate(profile)} className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded" title="Duplicate">
                                        <Copy size={16} />
                                    </button>
                                    <button onClick={(e) => handleDeleteClick(e, profile.id)} className="p-2 text-stone-400 hover:text-red-400 hover:bg-stone-800 rounded" title="Delete">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                                <PrimaryButton onClick={() => onSelectProfile(profile)} className="bg-stone-800 hover:bg-stone-700 text-stone-200 border border-stone-700">
                                    Use for Export
                                </PrimaryButton>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmationModal
                isOpen={deleteModalState.isOpen}
                onClose={() => setDeleteModalState({ isOpen: false, profileId: null })}
                onConfirm={handleConfirmDelete}
                title="Delete Profile"
                message="Are you sure you want to delete this export profile? This action cannot be undone."
            />
        </div>
    );
};

// --- RICH EDITOR COMPONENT ---

const ProfileEditor = ({ initialProfile, onSave, onCancel }) => {
    const [profile, setProfile] = useState(initialProfile);
    const [activeTab, setActiveTab] = useState(SECTIONS[0].id);
    const [availableTemplates, setAvailableTemplates] = useState([]);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (profile.output_style !== 'html' && profile.output_style !== 'pdf') {
                setAvailableTemplates([]);
                return;
            }
            try {
                const res = await fetch(`/api/export/templates?format=${profile.output_style}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    setAvailableTemplates(await res.json());
                }
            } catch (e) { console.error(e); }
        };
        fetchTemplates();
    }, [profile.output_style]);

    // Initial state normalization: Ensure config.sections is an array of objects
    // [ {id: 'general', fields: [...] }, ... ]
    const [sectionConfig, setSectionConfig] = useState(() => {
        const initial = profile.config?.sections || [];
        // Convert old string-array format if present
        if (initial.length > 0 && typeof initial[0] === 'string') {
            return initial.map(id => ({ id, fields: SECTIONS.find(s => s.id === id)?.fields.map(f => f.id) || [] }));
        }
        return initial;
    });

    // Helper to get config for a section
    const getSectionState = (sectionId) => {
        return sectionConfig.find(s => s.id === sectionId) || { id: sectionId, fields: [], enabled: false };
    };

    // Toggle Section Enable/Disable
    const toggleSection = (sectionId) => {
        setSectionConfig(prev => {
            const exists = prev.find(s => s.id === sectionId);
            if (exists) {
                // If exists, remove it (disable)
                return prev.filter(s => s.id !== sectionId);
            } else {
                // Add it, with ALL fields enabled by default for convenience? Or none?
                // Let's default to ALL fields for EZ usage
                const sectionDef = SECTIONS.find(s => s.id === sectionId);
                return [...prev, { id: sectionId, fields: sectionDef.fields.map(f => f.id) }];
            }
        });
    };

    // Toggle Field
    const toggleField = (sectionId, fieldId) => {
        setSectionConfig(prev => {
            const sectionIndex = prev.findIndex(s => s.id === sectionId);
            if (sectionIndex === -1) return prev; // Should not happen if UI is correct

            const section = prev[sectionIndex];
            const hasField = section.fields.includes(fieldId);

            let newFields;
            if (hasField) {
                newFields = section.fields.filter(f => f !== fieldId);
            } else {
                newFields = [...section.fields, fieldId];
            }

            const newSections = [...prev];
            newSections[sectionIndex] = { ...section, fields: newFields };
            return newSections;
        });
    };

    const handleSaveClick = () => {
        // Save full config
        const payload = {
            ...profile,
            config: {
                sections: sectionConfig // This now includes filtered fields
            }
        };
        onSave(payload);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="flex flex-col h-full bg-stone-900 border border-stone-800 rounded-lg overflow-hidden">
            {/* Editor Header */}
            <div className="px-6 py-4 bg-stone-950 border-b border-stone-800 flex justify-between items-center shrink-0">
                <input
                    type="text"
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    placeholder="Profile Name (e.g. Marketing Export)"
                    className="bg-transparent text-xl font-bold text-white focus:outline-none border-b border-transparent focus:border-stone-700 w-1/2"
                />
                <div className="flex gap-2">
                    <GhostButton onClick={onCancel}>Cancel</GhostButton>
                    <PrimaryButton onClick={handleSaveClick} className="gap-2">
                        <Save size={16} /> Save Profile
                    </PrimaryButton>
                </div>
            </div>

            {/* Description & Style - Quick Bar */}
            <div className="px-6 py-3 bg-stone-950/50 border-b border-stone-800 flex gap-4 shrink-0">
                <input
                    type="text"
                    name="description"
                    value={profile.description}
                    onChange={handleChange}
                    placeholder="Short description..."
                    className="flex-1 bg-stone-900 border border-stone-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                />
                <select
                    name="output_style"
                    value={profile.output_style}
                    onChange={handleChange}
                    className="w-48 bg-stone-900 border border-stone-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                    <option value="html_table">Excel (.xlsx)</option>
                    <option value="csv">CSV (.csv)</option>
                    <option value="html">HTML Datasheet (.html)</option>
                    <option value="pdf">PDF (.pdf)</option>
                </select>
            </div>

            {/* Template Selection */}
            {(profile.output_style === 'html' || profile.output_style === 'pdf') && (
                <div className="px-6 py-2 bg-stone-950/50 border-b border-stone-800 flex items-center gap-4 shrink-0">
                    <label className="text-sm font-bold text-stone-400">Template:</label>
                    <select
                        name="template"
                        value={profile.template || ''}
                        onChange={handleChange}
                        className="flex-1 bg-stone-900 border border-stone-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                    >
                        {availableTemplates.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-0">
                {/* 1. TABS (Scrollable) */}
                <div className="bg-stone-950 border-b border-stone-800 overflow-x-auto">
                    <div className="flex px-2">
                        {SECTIONS.map(section => {
                            const isEnabled = sectionConfig.some(s => s.id === section.id);
                            const isActive = activeTab === section.id;

                            return (
                                <div
                                    key={section.id}
                                    className={`
                                        group flex items-center gap-2 px-4 py-3 border-b-2 cursor-pointer transition-all whitespace-nowrap select-none
                                        ${isActive ? 'border-blue-500 bg-stone-900 text-white' : 'border-transparent text-stone-500 hover:text-stone-300 hover:bg-stone-900/50'}
                                    `}
                                    onClick={() => {
                                        // If clicking the checkbox area, toggle. Else switch tab.
                                        // Actually better to have distinct click zones? 
                                        // Let's make entire tab clickable to switch, Checkbox handles toggle.
                                        setActiveTab(section.id);
                                    }}
                                >
                                    <div
                                        onClick={(e) => { e.stopPropagation(); toggleSection(section.id); }}
                                        className={`rounded-sm transition-colors ${isEnabled ? 'text-green-500' : 'text-stone-600 group-hover:text-stone-500'}`}
                                    >
                                        {isEnabled ? <CheckSquare size={18} /> : <Square size={18} />}
                                    </div>
                                    <span className="font-bold text-sm">{section.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 2. TAB CONTENT (Field Selection) */}
                <div className="flex-1 overflow-y-auto p-8 bg-stone-900">
                    <div className="max-w-4xl mx-auto">
                        {SECTIONS.map(section => {
                            if (activeTab !== section.id) return null;
                            const sectionState = getSectionState(section.id);

                            return (
                                <div key={section.id} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                    <div className="mb-6 flex items-center justify-between">
                                        <div>
                                            <h2 className="text-xl font-bold text-white mb-1">{section.label} Settings</h2>
                                            <p className="text-stone-400 text-sm">Select the fields you want to include in the export for this section.</p>
                                        </div>
                                        {!sectionState.id && ( // If not enabled
                                            <div className="px-4 py-2 bg-stone-800/50 rounded-lg text-yellow-500 text-sm border border-yellow-500/20">
                                                This section is currently disabled. Check the box in the tab to enable it.
                                            </div>
                                        )}
                                    </div>

                                    {/* Fields Grid */}
                                    <div className={`grid grid-cols-2 gap-4 ${!sectionState.id ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
                                        {section.fields.map(field => {
                                            const isChecked = sectionState.fields?.includes(field.id);
                                            return (
                                                <div
                                                    key={field.id}
                                                    onClick={() => toggleField(section.id, field.id)}
                                                    className={`
                                                        flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all
                                                        ${isChecked
                                                            ? 'bg-blue-900/20 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
                                                            : 'bg-stone-950 border-stone-800 hover:border-stone-700 hover:bg-stone-900'
                                                        }
                                                    `}
                                                >
                                                    <div className={`
                                                        w-5 h-5 rounded flex items-center justify-center border transition-colors
                                                        ${isChecked ? 'bg-blue-500 border-blue-500' : 'bg-stone-900 border-stone-700'}
                                                    `}>
                                                        {isChecked && <CheckSquare size={14} className="text-white" />}
                                                    </div>
                                                    <div>
                                                        <div className={`font-bold ${isChecked ? 'text-white' : 'text-stone-400'}`}>
                                                            {field.label}
                                                        </div>
                                                        <div className="text-xs text-stone-600 font-mono mt-0.5">{field.id}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
